import { Routes, Route, Navigate } from 'react-router-dom'
import { useWorkspaceStore } from '@/store/workspace'
import { useEffect, useState } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import AppShell from '@/components/AppShell'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import Applications from '@/pages/Applications'
import ApplicationDetail from '@/pages/ApplicationDetail'
import Jobs from '@/pages/Jobs'
import Settings from '@/pages/Settings'

function RequireAuth({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null | 'loading'>('loading')
    const { workspaceId, setWorkspace } = useWorkspaceStore()

    useEffect(() => {
        return onAuthStateChanged(auth, async u => {
            setUser(u)
            if (u) {
                const { doc, getDoc, setDoc } = await import('firebase/firestore')
                const { db } = await import('@/lib/firebase')
                // Check and provision default workspace
                const wsRef = doc(db, 'workspaces', u.uid)
                try {
                    const wsSnap = await getDoc(wsRef)
                    if (!wsSnap.exists()) {
                        await setDoc(wsRef, {
                            name: 'My Company Workspace',
                            ownerId: u.uid,
                            plan: 'starter',
                            createdAt: new Date().toISOString()
                        })
                        await setDoc(doc(db, 'workspaces', u.uid, 'members', u.uid), {
                            role: 'admin',
                            joinedAt: new Date().toISOString()
                        })
                    }
                    setWorkspace(u.uid, wsSnap.exists() ? wsSnap.data()?.name || 'My Company Workspace' : 'My Company Workspace', 'starter')
                } catch (err) {
                    console.error("Failed to provision workspace:", err)
                    // Fallback to setting workspace ID even if doc check fails (legacy/permission transition)
                    setWorkspace(u.uid, 'My Company Workspace', 'starter')
                }
            }
        })
    }, [setWorkspace])

    if (user === 'loading') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <div className="spinner" />
            </div>
        )
    }

    return user ? <>{children}</> : <Navigate to="/login" replace />
}

export default function App() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route
                path="/*"
                element={
                    <RequireAuth>
                        <AppShell>
                            <Routes>
                                <Route index element={<Navigate to="/dashboard" replace />} />
                                <Route path="dashboard" element={<Dashboard />} />
                                <Route path="applications" element={<Applications />} />
                                <Route path="applications/:appId" element={<ApplicationDetail />} />
                                <Route path="jobs" element={<Jobs />} />
                                <Route path="settings" element={<Settings />} />
                                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                            </Routes>
                        </AppShell>
                    </RequireAuth>
                }
            />
        </Routes>
    )
}
